import apiClient from "./index";

/**
 * Get all activities with pagination.
 * @param {number} limit - Number of records to fetch.
 * @param {string|null} lastEvaluatedKey - Last evaluated key for pagination.
 */
export const getActivities = async (limit = 10, lastEvaluatedKey = null) => {
    try {
        const { data } = await apiClient.get("/activities", {
            params: {
                limit,
                lastEvaluatedKey,
            },
        });
        return data; // Should return { activities: [...], pagination: {...} }
    } catch (error) {
        console.error("Failed to fetch activities:", error);
        throw new Error(
            error.response?.data?.message || "Error fetching activities"
        );
    }
};

/**
 * Fetch a single activity by ID.
 * @param {string} activityId - ID of the activity to fetch.
 */
export const getActivity = async (activityId) => {
    try {
        const { data } = await apiClient.get(`/activities/${activityId}`);
        return data; // Should return the activity object
    } catch (error) {
        console.error("Failed to fetch activity:", error);
        throw new Error(
            error.response?.data?.message || "Error fetching activity"
        );
    }
};

/**
 * Create a new activity.
 * @param {Object} activity - The activity details to create.
 * @param {string} activity.title - Title of the activity.
 * @param {string} activity.description - Description of the activity.
 * @param {string} activity.type - Type of the activity (e.g., event).
 * @param {string} activity.startTime - Start time of the activity in ISO format.
 * @param {string} activity.endTime - End time of the activity in ISO format.
 * @param {string} activity.location - Location of the activity.
 */
export const createActivity = async (activity) => {
    try {
        const { data } = await apiClient.post("/activities", activity);
        return data.activity; // Return the activity object from the response
    } catch (error) {
        console.error("Failed to create activity:", error);
        throw new Error(
            error.response?.data?.message || "Error creating activity"
        );
    }
};

/**
 * Update an existing activity.
 * @param {string} activityId - ID of the activity to update.
 * @param {Object} updates - Updates to apply to the activity.
 * @param {string} [updates.title] - Updated title of the activity.
 * @param {string} [updates.description] - Updated description of the activity.
 * @param {string} [updates.type] - Updated type of the activity.
 * @param {string} [updates.startTime] - Updated start time of the activity in ISO format.
 * @param {string} [updates.endTime] - Updated end time of the activity in ISO format.
 * @param {string} [updates.location] - Updated location of the activity.
 */
export const updateActivity = async (activityId, updates) => {
    try {
        const { data } = await apiClient.put(`/activities/${activityId}`, updates);
        return data; // Should return the updated activity object
    } catch (error) {
        console.error("Failed to update activity:", error);
        throw new Error(
            error.response?.data?.message || "Error updating activity"
        );
    }
};

/**
 * Delete an activity by ID.
 * @param {string} activityId - ID of the activity to delete.
 */
export const deleteActivity = async (activityId) => {
    try {
        await apiClient.delete(`/activities/${activityId}`);
        return { success: true }; // Should return success response
    } catch (error) {
        console.error("Failed to delete activity:", error);
        throw new Error(
            error.response?.data?.message || "Error deleting activity"
        );
    }
};
